import Cookies from 'js-cookie';
import * as LZString from 'lz-string';
import { MiniZincExtra, type Solution } from './MiniZincExtra';

type Cookie = {
    xCount?: number;
    yCount?: number;
    seatingPlanIsGap?: boolean[][];
    groups?: Array<[string, Group]>;
};
var cookie: Cookie | null = null;
const COOKIE_ROOT_NAME = 'cck-wtf-seating-constraint-solver';
try { cookie = JSON.parse(LZString.decompressFromBase64(Cookies.get(COOKIE_ROOT_NAME) ?? '')) as Cookie; } catch (e) { console.log(e); }
if (cookie) {
    console.log('Cookie fetched:', cookie);
} else {
    console.log('No cookie');
    cookie = {};
}
function updateCookie() {
    if (cookie === null) { cookie = {}; }
    cookie.xCount = xCount;
    cookie.yCount = yCount;
    cookie.seatingPlanIsGap = seatingPlanIsGap;
    cookie.groups = Array.from(groups);

    // ? cookie set will fail if cookie size > 4096 bytes
    const cookieString = JSON.stringify(cookie);
    const compressed = LZString.compressToBase64(cookieString);
    Cookies.set(COOKIE_ROOT_NAME, compressed);
}

/* -------------------------------------------------------------------------- */

var xCount = cookie.xCount ?? 5;
var yCount = cookie.yCount ?? 3;
const seatingPlanIsGap: boolean[][] = cookie.seatingPlanIsGap ?? Array(yCount).fill([]).map(() => Array(xCount).fill(false));
const groups: Map<string, Group> = new Map(cookie.groups ?? []);

export type Group = {
    colour: string;
    size: number;
};

// setInterval(() => {
//     const memory = (performance as any).memory;
//     if (!memory) return;
//     console.log(`Memory: ${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`);
// }, 1000);

// setInterval(() => {
//     try {
//         throw new Error("Test error");
//     } catch { }
// }, 500);

/* -------------------------------------------------------------------------- */
const planContainerBoxElement = document.getElementById("planContainerBox") as HTMLDivElement;
document.addEventListener("DOMContentLoaded", onResize);
window.addEventListener("resize", onResize);
function onResize() {
    const rect = planContainerBoxElement.getBoundingClientRect();
    planContainerBoxElement.style.setProperty('--width', `${rect.width}px`);
    planContainerBoxElement.style.setProperty('--height', `${rect.height}px`);
}

const planContainerElement = document.getElementById("planContainer") as HTMLDivElement;

let isDragging = false;
let dragValue = false;
let lastMousePos = { x: 0, y: 0 };
planContainerElement.addEventListener("mousedown", (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains("seat")) {
        const x = parseInt(target.getAttribute("data-x")!);
        const y = parseInt(target.getAttribute("data-y")!);
        dragValue = !seatingPlanIsGap[y]![x];
        seatingPlanIsGap[y]![x] = dragValue;
        target.classList.toggle("disallowed", dragValue);
    }
    isDragging = true;
    lastMousePos = { x: e.clientX, y: e.clientY };
    e.preventDefault();
    updateCookie();
});
document.addEventListener("mousemove", (e) => {
    if (!isDragging) return;

    // interpolate mouse movement to avoid gaps when moving too fast
    const dist = Math.hypot(e.clientX - lastMousePos.x, e.clientY - lastMousePos.y);
    const steps = Math.ceil(dist / 5);
    for (let i = 1; i <= steps; i++) {
        const lerpX = lastMousePos.x + (e.clientX - lastMousePos.x) * (i / steps);
        const lerpY = lastMousePos.y + (e.clientY - lastMousePos.y) * (i / steps);
        const el = document.elementFromPoint(lerpX, lerpY) as HTMLElement | null;
        if (!el || !el.classList.contains("seat")) continue;

        const x = parseInt(el.getAttribute("data-x")!);
        const y = parseInt(el.getAttribute("data-y")!);
        if (seatingPlanIsGap[y]![x] === dragValue) continue;
        seatingPlanIsGap[y]![x] = dragValue;
        el.classList.toggle("disallowed", dragValue);
    }

    lastMousePos = { x: e.clientX, y: e.clientY };
    e.preventDefault();
    updateCookie();
});
document.addEventListener("mouseup", () => {
    isDragging = false;
});

const rowTemplate = document.getElementById("rowTemplate") as HTMLTemplateElement;
const seatTemplate = document.getElementById("seatTemplate") as HTMLTemplateElement;
redrawSeatingPlan();
function redrawSeatingPlan() {
    planContainerElement.innerHTML = "";
    seatingPlanIsGap.forEach((row, y) => {
        const rowElement = rowTemplate.content.cloneNode(true) as HTMLDivElement;
        const rowDiv = rowElement.querySelector(".row") as HTMLDivElement;

        row.forEach((seat, x) => {
            const seatElement = seatTemplate.content.cloneNode(true) as HTMLDivElement;
            const seatDiv = seatElement.querySelector(".seat") as HTMLDivElement;
            // seatDiv.textContent = getSeatName(x, y);
            seatDiv.setAttribute("data-x", x.toString());
            seatDiv.setAttribute("data-y", y.toString());
            seatDiv.classList.toggle("disallowed", seatingPlanIsGap[y]![x]);
            rowDiv.appendChild(seatDiv);
        });

        planContainerElement.appendChild(rowDiv);
    });

    planContainerElement.style.setProperty('--xCount', xCount.toString());
    planContainerElement.style.setProperty('--yCount', yCount.toString());
}

function getSeatName(x: number, y: number) {
    var xString = "";
    var xCopy = x;
    do {
        xString = String.fromCharCode((xCopy % 26) + 65) + xString;
        xCopy = Math.floor(xCopy / 26) - 1;
    } while (xCopy >= 0);

    return `${xString}${y + 1}`;
}

/* -------------------------------------------------------------------------- */

const groupListElement = document.getElementById("groupList") as HTMLDivElement;
const groupTemplate = document.getElementById("groupTemplate") as HTMLTemplateElement;
const addGroupButton = document.getElementById("addGroupButton") as HTMLButtonElement;

addGroupButton.addEventListener("click", () => {
    const groupID = crypto.randomUUID();
    const colour = `hsl(${Math.random() * 360}, ${Math.random() * 80 + 20}%, ${Math.random() * 25 + 50}%)`;
    groups.set(groupID, { colour, size: 1 });
    redrawGroups();
    updateCookie();
});

redrawGroups();
function redrawGroups() {
    groupListElement.innerHTML = "";
    groups.forEach((group, id) => {
        const groupElement = groupTemplate.content.cloneNode(true) as HTMLDivElement;
        const groupDiv = groupElement.querySelector(".group") as HTMLDivElement;
        groupDiv.setAttribute("data-group-id", id);
        const sizeInput = groupElement.querySelector(".groupSizeInput") as HTMLInputElement;
        sizeInput.value = group.size.toString();
        sizeInput.addEventListener("change", () => {
            const value = parseInt(sizeInput.value);
            if (isNaN(value)) { return; }
            sizeInput.value = value.toString();
            groups.get(id)!.size = value;
            updateCookie();
        });
        const removeButton = groupElement.querySelector(".removeGroupButton") as HTMLButtonElement;
        removeButton.addEventListener("click", () => {
            groupDiv.remove();
            groups.delete(id);
            updateCookie();
        });
        const groupColourDiv = groupElement.querySelector(".groupColour") as HTMLDivElement;
        groupColourDiv.style.backgroundColor = group.colour;
        groupColourDiv.addEventListener("mouseenter", () => {
            planContainerElement.querySelectorAll(`.seat[data-group-id="${id}"]`).forEach(seat => {
                seat.classList.add("highlight");
            });
        });
        groupColourDiv.addEventListener("mouseleave", () => {
            planContainerElement.querySelectorAll('.seat').forEach(seat => {
                seat.classList.remove("highlight");
            });
        });

        groupListElement.appendChild(groupDiv);
    });
}

/* -------------------------------------------------------------------------- */

const xCountInput = document.getElementById("xCountInput") as HTMLInputElement;
const yCountInput = document.getElementById("yCountInput") as HTMLInputElement;
xCountInput.value = xCount.toString();
yCountInput.value = yCount.toString();

xCountInput.addEventListener("change", () => {
    const value = parseInt(xCountInput.value);
    if (isNaN(value)) { return; }
    xCount = value;
    resizeSeatingPlanArray();
    redrawSeatingPlan();
    updateCookie();
});
yCountInput.addEventListener("change", () => {
    const value = parseInt(yCountInput.value);
    if (isNaN(value)) { return; }
    yCount = value;
    resizeSeatingPlanArray();
    redrawSeatingPlan();
    updateCookie();
});

function resizeSeatingPlanArray() {
    while (seatingPlanIsGap.length < yCount) {
        seatingPlanIsGap.push(Array.from({ length: xCount }, () => false));
    }
    while (seatingPlanIsGap.length > yCount) {
        seatingPlanIsGap.pop();
    }
    seatingPlanIsGap.forEach(row => {
        while (row.length < xCount) {
            row.push(false);
        }
        while (row.length > xCount) {
            row.pop();
        }
    });
}

/* -------------------------------------------------------------------------- */

await MiniZincExtra.init();
const model = MiniZincExtra.newModel();

const solveButton = document.getElementById("solveButton") as HTMLButtonElement;
solveButton.addEventListener("click", () => {
    planContainerElement.classList.add("solving");

    console.log("Running solver with:", {
        xCount,
        yCount,
        seatingPlanIsGap,
        groups,
    });
    const groupsArray = Array.from(groups.entries()).map(([id, group]) => ({ ...group, id }));

    let optimised = false;
    const optimisedSolutions: Omit<Solution, '_objective'>[] = [];

    const solveProgress = model.solve({
        xCount,
        yCount,
        seatingPlanIsGap,
        groupsArray,
    });
    solveProgress.on('error', err => {
        const outputStatusElement = document.getElementById("outputStatus") as HTMLDivElement;
        outputStatusElement.textContent = `Error: ${err.message}`;
    });
    solveProgress.on('solution', solution => {
        if (optimised) {
            optimisedSolutions.push(solution.output?.json as Omit<Solution, '_objective'>);
        }

        const json = solution.output?.json as Solution;
        visualiseSolution(groupsArray, json);
    });
    solveProgress.on('status', status => {
        switch (status.status) {
            case 'OPTIMAL_SOLUTION':
                {
                    optimised = true;
                    break;
                }
            case 'ALL_SOLUTIONS':
                {
                    console.log(optimisedSolutions);
                    break;
                }
            default:
                break;
        }

        const outputStatusElement = document.getElementById("outputStatus") as HTMLDivElement;
        outputStatusElement.textContent = `Status: ${status.status}`;
    });
});

function visualiseSolution(groupsArray: (Group & { id: string })[], solutionJson: Solution) {
    planContainerElement.querySelectorAll('.seat').forEach(seat => {
        (seat as HTMLDivElement).style.backgroundColor = '';
        (seat as HTMLDivElement).removeAttribute('data-group-id');
    });
    groupsArray.forEach((group, gi) => {
        const seats = solutionJson[`group${gi}`] as { x: number, y: number }[];
        seats.forEach(seat => {
            const seatDiv = planContainerElement.querySelector(`.seat[data-x="${seat.x - 1}"][data-y="${seat.y - 1}"]`) as HTMLDivElement;
            seatDiv.setAttribute('data-group-id', group.id);
            seatDiv.style.backgroundColor = group.colour;
        });
    });

    if (solutionJson._objective !== undefined) {
        const outputObjectiveElement = document.getElementById("outputObjective") as HTMLDivElement;
        outputObjectiveElement.textContent = `Objective badness: ${solutionJson._objective.toString()}`;
    }
}